import React, { useCallback, useState, useEffect, useId } from 'react';
import { useApp } from '../../context/AppContext';
import { examinerScope } from '../../utils/examinerApi';
import { ExaminerError, useExaminer } from './ExaminerShared';
import ExaminerAvatar from './ExaminerAvatar';
import './examiner.css';

export const useExaminerProfiles = request => {
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [savedId, setSavedId] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(async () => {
    setLoaded(false);
    const list = await request('/profiles');
    if (!Array.isArray(list)) throw new Error('Invalid examiner profile catalog. Reload examiner preferences.');
    const preference = await request('/preferences');
    const enabled = list.filter(item => item.enabled !== false);
    setProfiles(enabled);
    setSavedId(preference.examinerProfileId);
    setUpdatedAt(preference.updatedAt);
    setSelectedId(preference.examinerProfileId || (enabled.some(item => item.id === 'examiner-01') ? 'examiner-01' : ''));
    setLoaded(true);
  }, [request]);
  const save = async () => {
    if (!profiles.some(item => item.id === selectedId)) throw new Error('Select an available examiner before saving.');
    const data = await request('/preferences', { method: 'PUT', body: { examinerProfileId: selectedId } });
    if (!profiles.some(item => item.id === data.examinerProfileId)) throw new Error('The saved examiner is no longer in the catalog. Reload examiner preferences.');
    setSelectedId(data.examinerProfileId);
    setSavedId(data.examinerProfileId);
    setUpdatedAt(data.updatedAt);
  };
  return { profiles, selectedId, setSelectedId, savedId, updatedAt, loaded, load, save };
};

export const ExaminerProfilePicker = ({ model, disabled = false, onSave, canSave = true }) => {
  const group = useId();
  return <fieldset className="examiner-profiles" disabled={disabled}>
  <legend>Examiner appearance and voice</legend>
  <p>Choose a fictional animated examiner. Each appearance uses its server-assigned voice. Changes apply to new examinations only.</p>
  <div className="examiner-profile-grid">
    {model.profiles.map(profile => <label key={profile.id} className={`examiner-profile-card ${model.selectedId === profile.id ? 'selected' : ''}`}>
      <ExaminerAvatar profile={profile} compact />
      <span><input type="radio" name={group} value={profile.id} checked={model.selectedId === profile.id} onChange={() => model.setSelectedId(profile.id)} />{profile.name}</span>
      <span>{profile.description}</span>
      <span>Voice: {profile.voiceId}</span>
    </label>)}
  </div>
  {model.loaded && !model.profiles.length && <p role="status">No enabled examiner profiles. Ask your organization to configure the profile catalog.</p>}
  {model.selectedId && !model.profiles.some(item => item.id === model.selectedId) && <p role="alert">Your saved examiner is no longer available. Select another examiner and save your preference.</p>}
  {model.savedId && model.savedId === model.selectedId && <p role="status">Saved examiner preference{model.updatedAt ? ` - ${new Date(model.updatedAt).toLocaleString()}` : ''}.</p>}
  {canSave && <button type="button" disabled={disabled || !model.profiles.some(item => item.id === model.selectedId)} onClick={onSave}>Save examiner preference</button>}
  <p>Your selection is saved for your account in this organization, separate from administrator usage limits. No microphone access is needed to choose an appearance.</p>
</fieldset>;
};

const PreferencesForm = () => {
  const { request, run, busy, error, forbidden, onMissing } = useExaminer();
  const model = useExaminerProfiles(request);
  useEffect(() => { run(model.load); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  onMissing.current = model.load;
  return <section className="examiner" aria-label="Examiner preferences">
    <h3>AI Mock Examiner preferences</h3>
    <ExaminerError error={error} />
    <button type="button" disabled={busy} onClick={() => run(model.load)}>Reload examiner preferences</button>
    {model.loaded && <ExaminerProfilePicker model={model} disabled={busy} canSave={!forbidden.includes('/preferences')} onSave={() => run(model.save)} />}
  </section>;
};

const ExaminerPreferences = () => {
  const { currentUser, currentOrg } = useApp();
  if (!currentUser?.sessionId) return null;
  return <PreferencesForm key={`${examinerScope(currentUser)}:${currentOrg?.id}`} />;
};
export default ExaminerPreferences;
